import { useQuery } from '@tanstack/react-query';
import { skipNestedAttributes } from '../constants';
import { toast } from 'react-toastify';
import { InputFormPayload, initializeFormPayload } from '../components/CalculatorForm/types';
import { SystemDesign } from './useOptimizeInputs';
import callApiJson from './callApi';

export async function fetchSystemDesign(systemDesignId: string): Promise<SystemDesign> {
  const data = await callApiJson('/api/system-designs?id=' + systemDesignId);
  // if systemDesignData has error, return systemDesignData type systemDesign with "" values
  const systemDesignData: SystemDesign = skipNestedAttributes(data);
  const project_type_sd = systemDesignData?.project_type__c || 'Solar-only';
  if (project_type_sd === 'Storage-only' && systemDesignData){
    systemDesignData.epc_solar_per_watt_sd__c = 0
    systemDesignData.rooftop_pv_dc_kw__c = 0
    systemDesignData.ground_mount_pv_dc_kw__c = 0
    systemDesignData.carport_pv_dc_kw__c = 0
    systemDesignData.pv_size_dc_kw_sd__c = 0
    systemDesignData.pv_installer_cost_nom__c = 0
    systemDesignData.kwh_per_kw_sd__c = 0
    systemDesignData.pv_sunrock_procurement__c = 0
    systemDesignData.pv_additional_epc_cost_nom__c = 0
  }
  if (project_type_sd === 'Solar-only' && systemDesignData){
    systemDesignData.epc_bess_per_kwh_sd__c = 0
    systemDesignData.bess_installer_cost_nom__c = 0
    systemDesignData.bess_sunrock_procurement__c = 0
    systemDesignData.bess_additional_epc_cost_nom__c = 0
  }
  return systemDesignData;
}

const useCalculatorData = (
  selectedSystemDesign: string,
  selectedQuoteId: string,
  selectedOpportunityId: string
  ) => useQuery({
  queryKey: ['calculatorData', selectedSystemDesign, selectedQuoteId, selectedOpportunityId],
  queryFn: async () => {
    try {
      let systemDesignData: SystemDesign | undefined = undefined ;
      if (selectedSystemDesign) {
        systemDesignData = await fetchSystemDesign(selectedSystemDesign);
      }
      if (systemDesignData?.error || systemDesignData === undefined) {
        systemDesignData = {
          degradation_pcnt: "",
          pv_fcst_prod_yr1_jan_1: "",
          pv_fcst_prod_yr1_feb_2: "",
          pv_fcst_prod_yr1_mar_3: "",
          pv_fcst_prod_yr1_apr_4: "",
          pv_fcst_prod_yr1_may_5: "",
          pv_fcst_prod_yr1_jun_6: "",
          pv_fcst_prod_yr1_jul_7: "",
          pv_fcst_prod_yr1_aug_8: "",
          pv_fcst_prod_yr1_sep_9: "",
          pv_fcst_prod_yr1_oct_10: "",
          pv_fcst_prod_yr1_nov_11: "",
          pv_fcst_prod_yr1_dec_12: "",
          sr_equip_cost_nom: "",
          installer_cost_nom: "",
          sr_dev_cost_nom: "",
          additional_epc_cost_nom: "",
          third_party_dev_cost_nom: "",
          additional_epc_cost_per_watt: "",
          installer_cost_per_watt: "",
          sr_dev_cost_per_watt: "",
          sr_equip_cost_per_watt: "",
          third_party_dev_cost_per_watt: "",

          battery_pwr_rating_kw__c: "",
          battery_energy_rating_kwh__c: "",
          epc_solar_per_watt_sd__c: "",
          epc_bess_per_kwh_sd__c: "",
          project_type__c: "" ,
          rooftop_pv_dc_kw__c: "",
          ground_mount_pv_dc_kw__c: "",
          carport_pv_dc_kw__c: "",
          pv_size_dc_kw_sd__c: "",
          kwh_per_kw_sd__c: "",
          pv_installer_cost_nom__c: "",
          pv_sunrock_procurement__c: "",
          pv_additional_epc_cost_nom__c: "",
          bess_installer_cost_nom__c: "",
          bess_sunrock_procurement__c: "",
          bess_additional_epc_cost_nom__c: "",

        }
      }

      let data = await callApiJson('/api/opportunities?id=' + selectedOpportunityId);
      const opportunityData = skipNestedAttributes(data);

      let quotesData: InputFormPayload | undefined = undefined;
      let budgetData;
      if (selectedQuoteId) {
        data = await callApiJson('/api/quotes?id=' + selectedQuoteId);
        quotesData = skipNestedAttributes(data) as InputFormPayload;
        const budgetId = quotesData['Budget__c'];
        if (budgetId) {
          budgetData = await callApiJson('/api/budgets?id=' + budgetId);
        }
      }

      const referralData = await callApiJson('/api/referrals?opportunityId=' + selectedOpportunityId);


      const previousFormData = initializeFormPayload(quotesData, systemDesignData, opportunityData, budgetData, referralData);
      return {
        systemDesignData,
        opportunityData,
        selectedQuoteData: quotesData,
        previousFormData
      }
    } catch (error) {
      console.log(error);
      toast.warning('Failed to retrieve data on outputs, retry' + error.toString(), {
        position: toast.POSITION.TOP_RIGHT
      });
      throw new Error("Failed to retrieve data on outputs");
    }
  },
  enabled: !!selectedOpportunityId,
});

export default useCalculatorData;
